import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Typography } from '../components/Typography';
import { UserContext } from '../context/UserContext';
import {
  My_Kfh_Read_Saved_PropertiesDocument,
  My_Kfh_Read_Saved_Properties_IdsDocument,
  useMykfh_Create_Saved_PropertyMutation,
  useMykfh_Delete_Saved_PropertyMutation,
  useMykfh_Update_Saved_PropertyMutation,
} from '../types/graphqlHelpers';
import { isBrowser } from '../utils/isBrowser';
import { AUTH_TOKEN } from './useAuth';
import { useDataLayerPush } from './useDataLayerPush';

export interface SavePropertyProps {
  handleSaveProperty: () => void;
  isSaved: boolean;
  loading: boolean;
  savingLoading: boolean;
  deleteLoading: boolean;
  updateLoading: boolean;
  mutationIsComplete: boolean;
  alertsEnabled: boolean;
  handleUpdateProperty: any;
}

const ToastMessage = ({ toastName, toastTitle }) => (
  <div>
    <Typography variant="titleSmall">{toastTitle}</Typography>
    <Typography variant="bodyLarge">{toastName}</Typography>
  </div>
);

export function useSaveProperty(propertyUri, propertyName, property): SavePropertyProps {
  const [isSaved, setIsSaved] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [mutationIsComplete, setMutationIsComplete] = useState(false);
  const [savedPropertyId, setSavedPropertyId] = useState<string>();
  const [myKfhCreateSavedPropertyMutation, { data: savingData, loading: savingLoading, error: savingError }] =
    useMykfh_Create_Saved_PropertyMutation();
  const [myKfhDeleteSavedPropertyMutation, { data: deleteData, loading: deleteLoading, error: deleteError }] =
    useMykfh_Delete_Saved_PropertyMutation();
  const [myKfhUpdateSavedPropertyMutation, { loading: updateLoading, error: updateError }] =
    useMykfh_Update_Saved_PropertyMutation();
  const { savedProperties } = useContext(UserContext);
  const { dataLayerPush } = useDataLayerPush();

  // Fetch saved properties

  useEffect(() => {
    function checkSuccess() {
      // Working out if property is saved or not...

      const matchedProperties = savedProperties
        ? savedProperties.find((property) => property.uri === propertyUri)
        : undefined;

      if (matchedProperties) {
        const { id, deliverAlert } = matchedProperties;
        setIsSaved(true);
        setAlertsEnabled(deliverAlert as boolean);
        setSavedPropertyId(id as string);
      } else {
        setIsSaved(false);
      }
    }
    checkSuccess();
  }, [savedProperties, propertyUri]);

  const handleSaveProperty = () => {
    if (isSaved) {
      setIsSaved(false);
      myKfhDeleteSavedPropertyMutation({
        variables: {
          myKfhDeleteSavedProperty: {
            id: savedPropertyId,
          },
        },

        refetchQueries: [
          {
            query: My_Kfh_Read_Saved_Properties_IdsDocument,
          },
          {
            query: My_Kfh_Read_Saved_PropertiesDocument,
          },
        ],
        awaitRefetchQueries: true,
        onCompleted: (data) => {
          if (data?.myKfh?.myKfhDeleteSavedProperty?.success) {
            setMutationIsComplete(true);
            dataLayerPush({
              event: 'removePropertySave',
              propertyClickDetails: property,
            });
          }
        },
      });
    } else {
      setIsSaved(true);
      myKfhCreateSavedPropertyMutation({
        variables: {
          myKfhCreateSavedProperty: {
            propertyUri,
          },
        },
        refetchQueries: [
          {
            query: My_Kfh_Read_Saved_Properties_IdsDocument,
          },
          {
            query: My_Kfh_Read_Saved_PropertiesDocument,
          },
        ],
        awaitRefetchQueries: true,
        onCompleted: (data) => {
          if (data?.myKfh?.myKfhCreateSavedProperty?.success) {
            toast.success(<ToastMessage toastName={propertyName.replace('<br />', '')} toastTitle="Property saved" />);
            setMutationIsComplete(true);
            dataLayerPush({
              event: 'propertySave',
              propertyClickDetails: property,
            });
          }
        },
      });
    }
  };

  // Handle saving/deleting property

  const handleUpdateProperty = () => {
    myKfhUpdateSavedPropertyMutation({
      variables: {
        myKfhUpdateSavedProperty: {
          id: savedPropertyId,
          deliverAlert: !alertsEnabled,
        },
      },
      refetchQueries: [
        {
          query: My_Kfh_Read_Saved_Properties_IdsDocument,
        },
        {
          query: My_Kfh_Read_Saved_PropertiesDocument,
        },
      ],
      awaitRefetchQueries: true,
      update: () => {
        toast.success(<ToastMessage toastName={propertyName.replace('<br />', '')} toastTitle="Alert updated" />);
        setAlertsEnabled(!alertsEnabled);
      },
    });
  };

  const loading = savingLoading || deleteLoading || updateLoading;
  return {
    handleSaveProperty,
    handleUpdateProperty,
    isSaved,
    loading,
    savingLoading,
    deleteLoading,
    updateLoading,
    mutationIsComplete,
    alertsEnabled,
  };
}
